import React, { useContext, useLayoutEffect, useState } from 'react';
import { graphql, Link } from 'gatsby';
import { css } from '@emotion/react';
import Image from 'gatsby-plugin-sanity-image';
import { ThemeContext } from '../components/theme-provider';
import SEO from '../components/seo';
import FullImage from '../components/full-image';
import Button from '../components/button';
import PortableText from '../components/portable-text';
import PDF from '../components/pdf';
import Gallery from '../components/gallery';
import Accordion from '../components/accordion';

export default function About({ data }) {
  const [allowBuildingInteraction, setAllowBuildingInteraction] = useState(false);

  const theme = useContext(ThemeContext);

  useLayoutEffect(() => {
    theme.setGrayFooter(true);
  }, []);

  function dateDiff(d1, d2) {
    return (d1.getTime() - d2.getTime()) / (1000 * 60 * 60 * 24);
  }

  return (
    <div
      css={css`
        background-color: var(--white);
      `}
    >
      <SEO
        title="About"
        description={(data.about._rawContent) ? data.about._rawContent.map(el => el.children).flat().map((el) => el?.text)[0] : null}
        imageID={(data.about.featureImage) ? data.about.featureImage.asset._id : null}
      />
      <div
        css={css`
          background-color: var(--primary);
        `}
      >
        <FullImage
          text={`
            About/
          `}
          image={data.about.featureImage}
          includeTitle
        />
      </div>
      <div
        css={css`
          margin: 0 var(--margin);
        `}
      >
        <div
          className="type--45"
          css={css`
            margin-bottom: var(--bigMargin);
          `}
        >
          <PortableText
            blocks={data.about._rawContent}
          />
        </div>
        <div
          css={css`
            margin-bottom: var(--bigMargin);
          `}
        >
          <Accordion
            title="Team"
          >
            {data.about._rawTeamIntro && <div
              css={css`
                margin-bottom: var(--margin);
              `}
            >
              <PortableText
                blocks={data.about._rawTeamIntro}
              />
            </div>}
            {data.about.staff.length > 0 && data.about.staff
              .map((person, i) => (
                <div
                  key={i}
                >
                  {person._rawContent && <div
                    css={css`
                      text-decoration: underline;
                      cursor: pointer;
                      transition: color 0.6s var(--curve);

                      &:hover {
                        color: var(--primary);
                        transition: color 0s;
                      }
                    `}
                    onClick={() => { theme.setModalContent(person._rawContent); theme.setModalTitle(person.name); theme.setModalImage(person.image); theme.setShowModal(true); }}
                  >
                    <span css={css`text-transform: uppercase;`}>{person.name}</span>{(person.position) ? `, ${person.position}` : ''}
                  </div>}
                  {!person._rawContent && <div>
                    <span css={css`text-transform: uppercase;`}>{person.name}</span>{(person.position) ? `, ${person.position}` : ''}
                  </div>}
                </div>
              ))
            }
          </Accordion>
          {data.about.curators.length > 0 && <Accordion
            title="Artform Advisors"
          >
            {data.about._rawAdvisorsIntro && <div
              css={css`
                margin-bottom: var(--margin);
              `}
            >
              <PortableText
                blocks={data.about._rawAdvisorsIntro}
              />
            </div>}
            {data.about.curators.length > 0 && data.about.curators
              .map((person, i) => (
                <div
                  key={i}
                >
                  {person._rawContent && <div
                    css={css`
                      text-decoration: underline;
                      cursor: pointer;
                      transition: color 0.6s var(--curve);

                      &:hover {
                        color: var(--primary);
                        transition: color 0s;
                      }
                    `}
                    onClick={() => { theme.setModalContent(person._rawContent); theme.setModalTitle(person.name); theme.setModalImage(person.image); theme.setShowModal(true); }}
                  >
                    <span css={css`text-transform: uppercase;`}>{person.name}</span>{(person.position) ? `, ${person.position}` : ''}
                  </div>}
                  {!person._rawContent && <div>
                    <span css={css`text-transform: uppercase;`}>{person.name}</span>{(person.position) ? `, ${person.position}` : ''}
                  </div>}
                </div>
              ))
            }
          </Accordion>}
          <Accordion
            title="Board"
          >
            {data.about._rawManagementIntro && <div
              css={css`
                margin-bottom: var(--margin);
              `}
            >
              <PortableText
                blocks={data.about._rawManagementIntro}
              />
            </div>}
            {data.about.managementCommittee.length > 0 && data.about.managementCommittee
              .map((person, i) => (
                <div
                  key={i}
                >
                  {person._rawContent && <div
                    css={css`
                      text-decoration: underline;
                      cursor: pointer;
                      transition: color 0.6s var(--curve);

                      &:hover {
                        color: var(--primary);
                        transition: color 0s;
                      }
                    `}
                    onClick={() => { theme.setModalContent(person._rawContent); theme.setModalTitle(person.name); theme.setModalImage(person.image); theme.setShowModal(true); }}
                  >
                    <span css={css`text-transform: uppercase;`}>{person.name}</span>{(person.position) ? `, ${person.position}` : ''}
                  </div>}
                  {!person._rawContent && <div>
                    <span css={css`text-transform: uppercase;`}>{person.name}</span>{(person.position) ? `, ${person.position}` : ''}
                  </div>}
                </div>
              ))
            }
          </Accordion>
          <Accordion
            title="Local Community Reference Group"
          >
            {data.about._rawReferenceGroupIntro && <div
              css={css`
                margin-bottom: var(--margin);
              `}
            >
              <PortableText
                blocks={data.about._rawReferenceGroupIntro}
              />
            </div>}
            {data.about.referenceGroup.length > 0 && data.about.referenceGroup
              .map((person, i) => (
                <div
                  key={i}
                >
                  {person._rawContent && <div
                    css={css`
                      text-decoration: underline;
                      cursor: pointer;
                      transition: color 0.6s var(--curve);

                      &:hover {
                        color: var(--primary);
                        transition: color 0s;
                      }
                    `}
                    onClick={() => { theme.setModalContent(person._rawContent); theme.setModalTitle(person.name); theme.setModalImage(person.image); theme.setShowModal(true); }}
                  >
                    <span css={css`text-transform: uppercase;`}>{person.name}</span>{(person.position) ? `, ${person.position}` : ''}
                  </div>}
                  {!person._rawContent && <div>
                    <span css={css`text-transform: uppercase;`}>{person.name}</span>{(person.position) ? `, ${person.position}` : ''}
                  </div>}
                </div>
              ))
            }
          </Accordion>
          {data.about.information.length > 0 && data.about.information
            .map((information, i) => (
              <Accordion
                key={i}
                title={information.title}
              >
                <PortableText
                  blocks={information._rawContent}
                />
              </Accordion>
            ))
          }
          <Accordion
            title="Annual Reports"
          >
            <div
              css={css`
                padding-left: var(--gutter);
              `}
            >
              {data.about.annualReports.length > 0 && data.about.annualReports
                .map((pdf, i) => (
                  <PDF
                    key={i}
                    link={pdf.file?.asset.url}
                    text={pdf.title}
                  />
                ))
              }
            </div>
          </Accordion>
        </div>
        <div
          className="type--45"
          css={css`
            margin-bottom: var(--bigMargin);
          `}
        >
          <h2
            className="type--70"
            css={css`
              margin-top: 5rem;
              margin-bottom: 5rem;
            `}
          >History/</h2>
          <div
            css={css`
              margin-bottom: var(--margin);

              & > div {
                margin-top: var(--margin);
              }
            `}
          >
            {data.about.buildingImages.length > 0 && <Gallery images={data.about.buildingImages} />}
          </div>
          <PortableText
            blocks={data.about._rawBuildingText}
          />
        </div>
      </div>
      <div
        css={css`
          margin-bottom: var(--bigMargin);

          iframe {
            width: 100%;
            height: 66.66vh;
            border: 0;
            pointer-events: ${(allowBuildingInteraction) ? 'auto' : 'none'};
          }
        `}
        dangerouslySetInnerHTML={{__html: data.about.buildingEmbed}}
        onClick={() => setAllowBuildingInteraction(true)}
      ></div>
      {data.about.work
        .filter((job) => ((!job.closeDate) || (new Date().getTime() - 86400000 < new Date(job.closeDate).getTime())))
        .length > 0 && <div
        css={css`
          color: var(--black);
          padding: 5rem var(--margin);
        `}
      >
        <h2
          id="work-with-us"
          className="type--70"
          css={css`
            margin-top: 5rem;
            margin-bottom: 5rem;
          `}
        >Work with us/</h2>
        <div
          className="type--45"
          css={css`
            display: grid;
            grid-template-columns: repeat(12, 1fr);
            gap: var(--gutter);
            margin-bottom: var(--bigMargin);
          `}
        >
          {data.about.work.length > 0 && data.about.work
            .filter((job) => ((!job.closeDate) || (new Date().getTime() - 86400000 < new Date(job.closeDate).getTime())))
            .map((job, i) => {
              let color = 'var(--green)';
              let status = 'Applications now open';

              if (job.closeDate) {
                const daysUntil = dateDiff(new Date(job.closeDate), new Date());

                if (daysUntil <= 7) {
                  color = 'var(--orange)';
                  status = `Applications closing ${new Date(job.closeDate).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}`;
                } else {
                  color = 'var(--red)';
                  status = `Applications closing soon, ${new Date(job.closeDate).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}`;
                }
              }

              return (
                <div
                  key={i}
                  id={job.slug.current}
                  css={css`
                    grid-column: span 6;
                    padding: var(--gutter);
                    border: 2px solid var(--black);

                    @media (max-width: 700px) {
                      grid-column: span 12;
                    }

                    a {
                      color: var(--black);

                      &:hover {
                        color: var(--primary);
                      }
                    }
                  `}
                >
                  <div
                    className="type--20"
                    css={css`
                      color: ${color};
                      margin-bottom: calc(var(--gutter) * 0.75);

                      &::before {
                        content: '';
                        display: inline-block;
                        width: 1.1rem;
                        height: 1.1rem;
                        border-radius: 50%;
                        background-color: ${color};
                        margin-right: 1rem;
                      }
                    `}
                  >
                    {status}
                  </div>
                  <div
                    css={css`
                      margin-bottom: var(--margin);
                    `}
                  ><span
                    css={css`
                    text-transform: uppercase;
                    `}
                  >{job.title}</span>: {job.description}</div>
                  {job.email && <div
                    className="type--20"
                  >
                    <a target="_blank" rel="noreferrer" href={`mailto:${job.email}`}>Email Us</a>
                  </div>}
                  {job.links.length > 0 && job.links
                    .map((link, i) => (
                      <div
                        className="type--20"
                        css={css`
                          margin-top: calc(var(--gutter) * 0.5);
                        `}
                      >
                        <a target="_blank" rel="noreferrer" href={link.url}>{link.title}</a>
                      </div>
                    ))
                  }
                  {job.files.length > 0 && job.files
                    .map((file, i) => (
                      <div
                        className="type--20"
                        css={css`
                          margin-top: calc(var(--gutter) * 0.5);
                        `}
                      >
                        <a target="_blank" rel="noreferrer" href={file.file.asset.url}>{file.title}</a>
                      </div>
                    ))
                  }
                </div>
            )})
          }
        </div>
      </div>}
    </div>
  );
}

export const query = graphql`
  query {
    about: sanityAbout(_id: { regex: "/(drafts\\\\.)?about/" }) {
      id
      featureImage {
        ...Image
        asset {
          description
        }
      }
      _rawContent(resolveReferences: {maxDepth: 10})
      annualReports {
        title
        file {
          asset {
            url
          }
        }
      }
      _rawTeamIntro(resolveReferences: {maxDepth: 10})
      staff {
        name
        image {
          ...Image
          asset {
            description
          }
        }
        position
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      _rawAdvisorsIntro(resolveReferences: {maxDepth: 10})
      curators {
        name
        image {
          ...Image
          asset {
            description
          }
        }
        position
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      _rawManagementIntro(resolveReferences: {maxDepth: 10})
      managementCommittee {
        name
        image {
          ...Image
          asset {
            description
          }
        }
        position
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      _rawReferenceGroupIntro(resolveReferences: {maxDepth: 10})
      referenceGroup {
        name
        image {
          ...Image
          asset {
            description
          }
        }
        position
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      information {
        title
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      _rawBuildingText(resolveReferences: {maxDepth: 10})
      buildingImages {
        ...Image
        asset {
          description
        }
      }
      buildingEmbed
      _rawHireText(resolveReferences: {maxDepth: 10})
      venues {
        title
        slug {
          current
        }
        featureImage {
          ...Image
        }
        suitability
        capacity
        dimensions
      }
      hireLink
      filmingPhotographyLink
      work {
        closeDate
        title
        slug {
          current
        }
        description
        email
        links {
          title
          url
        }
        files {
          title
          file {
            asset {
              url
            }
          }
        }
      }
    }
  }
`

